import FileSaver from "file-saver";
import mixpanel from "mixpanel-browser";
import PropTypes from "prop-types";
import React from "react";
import ReactDom from "react-dom";
import vCardJs from "vcards-js";
import "./styles.css";
import { useAppContext } from './../../../util'

const OVERLAY_STYLES = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(18, 18, 18, 0.6)",
  borderRadius: "10px",
  zIndex: 1000,
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "end",
};

const MODAL_STYLES = {
  backgroundColor: "white",
  borderTopLeftRadius: "10px",
  borderTopRightRadius: "10px",
  boxShadow: "0 2px 10px rgba(18, 18, 18, 0.6)",
  padding: "20px",
  boxSizing: "border-box",
  width: "100%",
};

export default function Card({ open, onClose, data }) {

  const { userId, pageId } = useAppContext()

  if (!open) return null;

  const date = new Date();
  const currentDate = `${date.getFullYear()}-${
    date.getMonth() + 1
  }-${date.getDate()}`;

  const {
    firstname,
    lastname,
    company,
    email,
    address1,
    address2,
    phone,
    position,
    city,
    state,
    zip,
    country,
    allowDownload,
    downloadButtonText,
  } = data;

  const downloadTxtFile = (vcfText) => {
    const blob = new Blob([vcfText], { type: "text/vcard;charset=utf-8" });
    FileSaver.saveAs(blob, `${currentDate}.vcf`);
  };

  const createVCard = () => {
    const vCard = vCardJs();
    vCard.firstName = firstname;
    vCard.lastName = lastname;
    vCard.organization = company;
    vCard.email = email;
    vCard.cellPhone = phone;
    vCard.workAddress.street = address1;
    vCard.workAddress.extended = address2;
    vCard.title = position;
    vCard.workAddress.locality = city;
    vCard.workAddress.region = state;
    vCard.workAddress.postalCode = zip;
    vCard.workAddress.country = country;
    return vCard.getFormattedString();
  };

  const handleDownload = () => {
    downloadTxtFile(createVCard());
    mixpanel.track(
      "Web Contact Card Downloaded", 
      {
        stack_id: pageId,
        distinct_id: userId,
      }
    )
  };

  return ReactDom.createPortal(
    <>
      <div style={OVERLAY_STYLES}>
        <div style={MODAL_STYLES}>
          <div className="card-container">
            <div className="card-header">
              <span className="card-title">Contact info</span>

              <span
                className="back-button"
                onClick={() => {
                  onClose();
                }}
              >
                <i className="fa-sharp fa-solid fa-close" />
              </span>
            </div>

            <div className="card-content">
              <div className="card-item">
                <span className="icon">
                  <i className="fa-sharp fa-solid fa-circle-user" />
                </span>

                <span className="title">{firstname + " " + lastname}</span>
              </div>

              {company && (
                <div className="card-company">
                  <span className="building-icon">
                    <i className="fa-sharp fa-solid fa-building" />
                  </span>

                  <div className="details">
                    <span className="company-name">{company}</span>
                    <span className="position">{position}</span>
                  </div>
                </div>
              )}

              {email && (
                <div className="card-email">
                  <span className="icon">
                    <i className="fa-sharp fa-solid fa-at" />
                  </span>
                  <span className="sub-title">{email}</span>
                </div>
              )}

              {phone && (
                <div className="card-phone">
                  <span className="icon">
                    <i className="fa-sharp fa-solid fa-phone" />
                  </span>

                  <span className="sub-title">{phone}</span>
                </div>
              )}

              {(country || state || zip || address1 || address2) && (
                <div className="card-address">
                  <span className="icon">
                    <i className="fa-sharp fa-solid fa-location-dot" />
                  </span>
                  <span className="address-detail">
                    {address1}
                    <br />
                    {address2}
                    <br />
                    {city} {state} {zip}
                    <br />
                    {country}
                  </span>
                </div>
              )}
            </div>

            {allowDownload && (
              <div className="download-button" onClick={handleDownload}>
                <span>{downloadButtonText}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.getElementById("portal")
  );
}

Card.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    company: PropTypes.string,
    email: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    phone: PropTypes.string,
    position: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string,
    allowDownload: PropTypes.bool,
    downloadButtonText: PropTypes.string,
  }).isRequired,
};
